/* DryEyeQ - Dry Eye Rescue
 * Vanilla JS. No dependencies. No persistence of any answer.
 * Answers live in memory for the current page view only: no cookies,
 * no localStorage, no sessionStorage, no network transmission of answers.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-deq]');
  if (!root) return;

  /* ------------------------------------------------------------------ *
   * Config and curated product data (rendered by Liquid at page load)
   * ------------------------------------------------------------------ */
  function readJson(id, fallback) {
    var el = document.getElementById(id);
    if (!el) return fallback;
    try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
  }
  var CONFIG = readJson('deq-config', {});
  var PRODUCTS = readJson('deq-products', []);

  /* ------------------------------------------------------------------ *
   * Screen order
   * ------------------------------------------------------------------ */
  var QUESTION_IDS = [
    'safety',
    'o1', 'o2', 'o3', 'o4', 'o5', 'o6',
    'p1', 'p2', 'p3', 'p4', 'p5', 'p6',
    'cl1', 'cl2'
  ];

  var els = {
    stage: root.querySelector('[data-deq-stage]'),
    intro: root.querySelector('[data-screen="intro"]'),
    prog: root.querySelector('[data-deq-prog]'),
    progFill: root.querySelector('[data-deq-prog-fill]'),
    progTxt: root.querySelector('[data-deq-prog-txt]'),
    status: root.querySelector('[data-deq-status]'),
    toast: root.querySelector('[data-deq-toast]')
  };
  /* ------------------------------------------------------------------ *
   * Screen mounting.
   *
   * Only the screen the patient is looking at exists in the document. Every
   * other screen, and every result variant, sits in an inert <template> and
   * is cloned into the stage on demand. Nothing inactive is ever ordinary
   * body copy, so crawlers, assistive technology, and "view source" all see
   * one state at a time.
   * ------------------------------------------------------------------ */
  var active = null;

  function tplFor(id) {
    return root.querySelector('template[data-tpl="' + id + '"]');
  }

  function fill(host, selector, id) {
    var slot = host.querySelector(selector);
    if (!slot) return null;
    slot.innerHTML = '';
    var t = tplFor(id);
    if (t) slot.appendChild(t.content.cloneNode(true));
    return slot;
  }

  function mount(id) {
    els.stage.innerHTML = '';
    if (id === 'intro') {
      els.intro.hidden = false;
      active = els.intro;
      return active;
    }
    var t = tplFor(id);
    if (!t) return null;
    els.intro.hidden = true;
    els.stage.appendChild(t.content.cloneNode(true));
    active = els.stage.querySelector('[data-screen]');
    if (active) active.hidden = false;
    return active;
  }

  /* ------------------------------------------------------------------ *
   * State
   * ------------------------------------------------------------------ */
  var state, current, history, completed;

  function reset() {
    completed = false;
    state = {
      safety: [],
      o1: null, o2: null, o3: null, o4: null, o5: null, o6: null,
      p1: [], p2: null, p3: null, p4: [], p5: null, p6: null,
      cl1: null, cl2: null
    };
    current = 'intro';
    history = [];
  }
  reset();

  /* ------------------------------------------------------------------ *
   * OSDI-6 scoring. Constantly 4, Mostly 3, Often 2, Sometimes 1, Never 0.
   * The OSDI-6 score is the plain sum of the six answers and is never
   * altered by any DryEyeQ question.
   * ------------------------------------------------------------------ */
  var OSDI_KEYS = ['o1', 'o2', 'o3', 'o4', 'o5', 'o6'];

  function osdiScore() {
    var total = 0;
    for (var i = 0; i < OSDI_KEYS.length; i++) {
      var v = state[OSDI_KEYS[i]];
      if (v !== null && v !== undefined) total += Number(v);
    }
    return total;
  }
  function osdiComplete() {
    return OSDI_KEYS.every(function (k) { return state[k] !== null; });
  }
  function bandFor(score) {
    if (score <= 3) return { key: 'normal', label: 'Normal Range' };
    if (score <= 8) return { key: 'mild', label: 'Mild to Moderate' };
    return { key: 'severe', label: 'Severe Symptoms' };
  }

  /* ------------------------------------------------------------------ *
   * Pattern scoring. Always recomputed from scratch from current answers,
   * never incremented on top of a previous result.
   * ------------------------------------------------------------------ */
  var PATTERN_RULES = {
    p1: {
      wake:      { night: 5 },
      later:     { mgd: 3, hydration: 1 },
      screens:   { mgd: 2, hydration: 1 },
      wind:      { mgd: 3, hydration: 1 },
      nopattern: { hydration: 2 }
    },
    p2: {
      noblur:  { hydration: 1 },
      clears:  { mgd: 4 },
      noclear: { doctor: 5 },
      unsure:  {}
    },
    p3: {
      nothing:   { hydration: 1 },
      crusting:  { lid: 5, mgd: 2 },
      itching:   { allergy: 5 },
      discharge: { doctor: 6 }
    },
    p4: {
      oneeye: { doctor: 5 },
      pain:   { doctor: 6 },
      light:  { doctor: 4 },
      none:   {}
    },
    p5: {
      nothing:    {},
      occasional: { hydration: 1 },
      daily:      { hydration: 1, doctor: 1 },
      failing:    { doctor: 5 }
    },
    p6: {
      none:     {},
      mgdbleph: { mgd: 6, lid: 2 },
      demodex:  { lid: 6 },
      other:    { doctor: 6 }
    },
    cl2: {
      notrelated: {},
      whilein:    { contact: 6, hydration: 1 },
      afterout:   { contact: 3, doctor: 3 },
      unsure:     { contact: 1 }
    }
  };

  var CATEGORIES = ['hydration', 'mgd', 'lid', 'night', 'allergy', 'contact'];

  /* Doctor score at which the eye-exam block leads the result. */
  var LOCATE_THRESHOLD = 8;

  function compute() {
    var s = {
      hydration: 0, mgd: 0, lid: 0, night: 0,
      allergy: 0, contact: 0, doctor: 0
    };

    function apply(map) {
      if (!map) return;
      for (var k in map) { if (Object.prototype.hasOwnProperty.call(map, k)) s[k] += map[k]; }
    }

    state.p1.forEach(function (v) { apply(PATTERN_RULES.p1[v]); });
    apply(PATTERN_RULES.p2[state.p2]);
    apply(PATTERN_RULES.p3[state.p3]);
    state.p4.forEach(function (v) { apply(PATTERN_RULES.p4[v]); });
    apply(PATTERN_RULES.p5[state.p5]);
    apply(PATTERN_RULES.p6[state.p6]);
    if (state.cl1 === 'yes') apply(PATTERN_RULES.cl2[state.cl2]);

    var osdi = osdiScore();
    var band = bandFor(osdi);

    /* Internal doctor-routing points only. The displayed OSDI-6 score
       never changes. */
    if (osdi > 8) s.doctor += 2;

    /* An exam leads the result only when two or more real concerns stack up.
       No single answer reaches 8 on its own, so "I already follow a routine
       and I am still struggling" or "one eye is worse" no longer take the
       result over by themselves. Either way the patient still gets the
       product pathway underneath: see renderResult. */
    var locate = s.doctor >= LOCATE_THRESHOLD;

    /* Ranked categories */
    var ranked = CATEGORIES.slice().sort(function (a, b) { return s[b] - s[a]; });
    var top = s[ranked[0]];
    var primary;

    if (top === 0) {
      primary = 'hydration';
    } else {
      var tied = CATEGORIES.filter(function (c) { return s[c] === top; });
      primary = tied.length === 1 ? tied[0] : breakTie(tied);
    }

    var secondary = null;
    for (var i = 0; i < ranked.length; i++) {
      if (ranked[i] !== primary && s[ranked[i]] > 0) { secondary = ranked[i]; break; }
    }

    return {
      osdi6_score: osdi,
      osdi6_band: band.key,
      osdi6_band_label: band.label,
      hydration_score: s.hydration,
      mgd_score: s.mgd,
      lid_score: s.lid,
      night_score: s.night,
      allergy_score: s.allergy,
      contact_score: s.contact,
      doctor_score: s.doctor,
      dryeyeq_primary_pattern: primary,
      dryeyeq_secondary_pattern: secondary,
      dryeyeq_primary_action: locate ? 'LOCATE' : 'SHOP',
      severeNotice: !locate && osdi > 8,
      elevateLocate: state.cl2 === 'afterout'
    };
  }

  /* Order of clinical specificity. The first rule whose condition holds and
     whose category is among the tied categories wins. */
  function breakTie(tied) {
    var rules = [
      { cat: 'mgd',       when: function () { return state.p6 === 'mgdbleph'; } },
      { cat: 'lid',       when: function () { return state.p6 === 'demodex'; } },
      { cat: 'allergy',   when: function () { return state.p3 === 'itching'; } },
      { cat: 'night',     when: function () { return state.p1.indexOf('wake') !== -1; } },
      { cat: 'contact',   when: function () { return state.cl1 === 'yes' && (state.cl2 === 'whilein' || state.cl2 === 'afterout'); } },
      { cat: 'mgd',       when: function () { return state.p2 === 'clears' || state.p1.indexOf('wind') !== -1 || state.p1.indexOf('later') !== -1; } },
      { cat: 'hydration', when: function () { return true; } }
    ];
    for (var i = 0; i < rules.length; i++) {
      if (tied.indexOf(rules[i].cat) !== -1 && rules[i].when()) return rules[i].cat;
    }
    var order = ['mgd', 'lid', 'night', 'allergy', 'contact', 'hydration'];
    for (var j = 0; j < order.length; j++) {
      if (tied.indexOf(order[j]) !== -1) return order[j];
    }
    return 'hydration';
  }

  /* ------------------------------------------------------------------ *
   * Product selection: priority 1, then 2, then 3, availability aware.
   * ------------------------------------------------------------------ */
  function pickProduct(route, exclude) {
    var pool = PRODUCTS.filter(function (p) {
      return p.r === route && p.av === true && (!exclude || p.h !== exclude);
    });
    if (!pool.length) return null;
    pool.sort(function (a, b) {
      var pa = a.pr || 9, pb = b.pr || 9;
      if (pa !== pb) return pa - pb;
      return (a.t || '').localeCompare(b.t || '');
    });
    return pool[0];
  }

  /* ------------------------------------------------------------------ *
   * Analytics. Engagement only. No answers, no score, no inferred condition.
   * ------------------------------------------------------------------ */
  function track(name) {
    try {
      document.dispatchEvent(new CustomEvent('dryeyeq:' + name));
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: name });
      }
    } catch (e) { /* analytics must never break the assessment */ }
  }

  /* ------------------------------------------------------------------ *
   * Navigation
   * ------------------------------------------------------------------ */
  function nextAfter(id) {
    if (id === 'intro') return 'safety';
    if (id === 'safety') return safetyFlagged() ? 'stop' : 'o1';
    if (id === 'o6') return 'score';
    if (id === 'score') return 'p1';
    if (id === 'cl1') return state.cl1 === 'yes' ? 'cl2' : 'result';
    if (id === 'cl2') return 'result';
    if (id === 'p6') return 'cl1';
    var i = QUESTION_IDS.indexOf(id);
    if (i !== -1 && i + 1 < QUESTION_IDS.length) return QUESTION_IDS[i + 1];
    return 'result';
  }

  function safetyFlagged() {
    return state.safety.some(function (v) { return v !== 'none'; });
  }

  function go(id, isBack) {
    if (id !== 'intro' && !tplFor(id)) return;
    if (!isBack && current) history.push(current);

    mount(id);
    if (!active) return;
    current = id;

    if (id === 'score') renderScore();
    if (id === 'result') renderResult();

    updateProgress();
    syncOptions(id);
    updateNav(id);

    var h = active.querySelector('[data-deq-focus]');
    if (h) { h.focus({ preventScroll: true }); }

    var top = root.getBoundingClientRect().top + window.pageYOffset - 24;
    window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'auto' });

    if (id === 'safety') track('dryeyeq_start');
    if (id === 'score') track('dryeyeq_osdi_complete');
    if (id === 'result') {
      track('dryeyeq_complete');
      completed = true;
      setCta2('Retake DryEyeQ');
    }
  }

  function back() {
    if (!history.length) return;
    var prev = history.pop();
    go(prev, true);
  }

  /* The secondary call to action under "Not Every DryEyeQ Ends With a Product"
     invites a first run, then offers a retake once one is done. */
  function setCta2(label) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-deq-cta2]'), function (b) {
      b.textContent = label;
    });
  }

  /* Progress is reported per section, never as a single long count. The
     patient sees "Symptom Score 1 of 6", then "Symptom Pattern 1 of 6". The
     bar underneath still fills across the whole assessment, but no total
     number of questions is ever displayed.

     The bar counts the screen the patient is on against one more than the
     number of questions, so it never reads as finished while a question is
     still open, and the denominator stays at 15 until the contact-lens branch
     is ruled out.

     The score screen is a milestone rather than a question and already
     announces itself, so it carries no progress row at all. */
  var SECTION_LABEL = {
    safety:  'Before We Start',
    osdi:    'Symptom Score',
    pattern: 'Symptom Pattern'
  };
  var OSDI_IDS = ['o1', 'o2', 'o3', 'o4', 'o5', 'o6'];
  var PATTERN_IDS = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'];

  function totalQuestions() { return state.cl1 === 'no' ? 14 : 15; }

  /* Returns { txt, sr } for the current screen, or null when no progress
     indicator belongs on it. */
  function sectionProgress(id) {
    if (id === 'safety') {
      return { txt: SECTION_LABEL.safety, sr: SECTION_LABEL.safety };
    }
    var i = OSDI_IDS.indexOf(id);
    if (i !== -1) {
      return {
        txt: SECTION_LABEL.osdi + ' \u00B7 ' + (i + 1) + ' of 6',
        sr: SECTION_LABEL.osdi + ', question ' + (i + 1) + ' of 6'
      };
    }
    i = PATTERN_IDS.indexOf(id);
    if (i !== -1) {
      return {
        txt: SECTION_LABEL.pattern + ' \u00B7 ' + (i + 1) + ' of 6',
        sr: SECTION_LABEL.pattern + ', question ' + (i + 1) + ' of 6'
      };
    }
    /* The contact-lens branch is part of the pattern section and carries no
       number, so answering it never enlarges a denominator the patient has
       already been shown. */
    if (id === 'cl1' || id === 'cl2') {
      return { txt: SECTION_LABEL.pattern, sr: SECTION_LABEL.pattern };
    }
    return null;
  }

  function updateProgress() {
    var info = sectionProgress(current);
    els.prog.hidden = !info;
    if (!info) return;

    var idx = QUESTION_IDS.indexOf(current);
    if (idx !== -1) {
      els.progFill.style.width =
        Math.round(((idx + 1) / (totalQuestions() + 1)) * 100) + '%';
    }
    els.progTxt.textContent = info.txt;
    els.status.textContent = info.sr;
  }

  function updateNav(id) {
    if (!active) return;
    var nav = active.querySelector('[data-deq-nav]');
    if (!nav) return;
    nav.classList.toggle('is-backable', history.length > 0);
    var cont = nav.querySelector('[data-deq-continue]');
    if (cont) cont.disabled = !hasAnswer(id);
  }

  function hasAnswer(id) {
    var v = state[id];
    if (Array.isArray(v)) return v.length > 0;
    return v !== null && v !== undefined;
  }

  /* ------------------------------------------------------------------ *
   * Option handling
   * ------------------------------------------------------------------ */
  /* Only the mounted screen has options in the document, so re-syncing after
     a reset is a single screen's worth of work. */
  function syncAllOptions() {
    if (current) syncOptions(current);
  }

  function syncOptions(id) {
    if (!active || active.getAttribute('data-screen') !== id) return;
    Array.prototype.forEach.call(active.querySelectorAll('.deq-opt'), function (btn) {
      var v = btn.getAttribute('data-value');
      var cur = state[id];
      var on = Array.isArray(cur) ? cur.indexOf(v) !== -1 : cur === v;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  root.addEventListener('click', function (evt) {
    var opt = evt.target.closest('.deq-opt');
    if (opt && root.contains(opt)) { handleOption(opt); return; }

    var next = evt.target.closest('[data-deq-next]');
    if (next && root.contains(next)) { evt.preventDefault(); go(nextAfter(current)); return; }

    var b = evt.target.closest('[data-deq-back]');
    if (b && root.contains(b)) { evt.preventDefault(); back(); return; }

    var start = evt.target.closest('[data-deq-start]');
    if (start && root.contains(start)) {
      evt.preventDefault();
      /* The same button starts the assessment and, once it has been finished
         in this page view, restarts it from a clean slate. */
      if (completed) { reset(); syncAllOptions(); }
      go('safety');
      return;
    }

    var retake = evt.target.closest('[data-deq-retake]');
    if (retake && root.contains(retake)) {
      evt.preventDefault();
      reset();
      mount('intro');
      current = 'intro';
      updateProgress();
      setCta2('Start My DryEyeQ');
      go('safety');
      return;
    }

    var atc = evt.target.closest('[data-deq-atc]');
    if (atc && root.contains(atc)) { evt.preventDefault(); addToCart(atc); return; }

    var link = evt.target.closest('[data-deq-track]');
    if (link && root.contains(link)) { track(link.getAttribute('data-deq-track')); }
  });

  function handleOption(btn) {
    var screen = btn.closest('[data-screen]');
    var id = screen.getAttribute('data-screen');
    var mode = screen.getAttribute('data-mode');
    var value = btn.getAttribute('data-value');
    var max = parseInt(screen.getAttribute('data-max') || '0', 10);

    if (mode === 'multi') {
      var arr = state[id].slice();
      var exclusive = btn.getAttribute('data-exclusive') === 'true';
      var pos = arr.indexOf(value);

      if (pos !== -1) {
        arr.splice(pos, 1);
      } else if (exclusive) {
        arr = [value];
      } else {
        arr = arr.filter(function (v) {
          var other = screen.querySelector('.deq-opt[data-value="' + v + '"]');
          return !(other && other.getAttribute('data-exclusive') === 'true');
        });
        arr.push(value);
        if (max && arr.length > max) arr.shift();
      }
      state[id] = arr;
      syncOptions(id);
      updateNav(id);
      return;
    }

    state[id] = value;
    syncOptions(id);
    /* Single-select advances. Small delay so the selected state is visible
       and screen readers register the change before the screen swaps. */
    window.setTimeout(function () {
      if (current === id) go(nextAfter(id));
    }, 180);
  }

  /* ------------------------------------------------------------------ *
   * Score screen
   * ------------------------------------------------------------------ */
  var SCORE_COPY = {
    normal: {
      body: "Your current symptom score falls within the normal range. That does not mean you can never have eye discomfort, so let's look at when your symptoms happen and what seems to trigger them.",
      cta: 'Continue'
    },
    mild: {
      body: 'Your answers show a meaningful level of eye symptoms. Your score measures how much your eyes are bothering you, but it cannot determine the cause.',
      cta: 'Find My Next Step'
    },
    severe: {
      body: 'Your symptom score is elevated. Products may still help with comfort, but an eye examination should be an important part of your next step.',
      cta: 'Continue'
    }
  };

  function renderScore() {
    var score = osdiScore();
    var band = bandFor(score);
    var copy = SCORE_COPY[band.key];

    active.querySelector('[data-deq-score-n]').textContent = String(score);
    var badge = active.querySelector('[data-deq-score-band]');
    badge.className = 'deq-band deq-band--' + band.key;
    badge.querySelector('[data-deq-score-band-label]').textContent = band.label;
    active.querySelector('[data-deq-score-copy]').textContent = copy.body;
    active.querySelector('[data-deq-score-cta]').textContent = copy.cta;
  }

  /* ------------------------------------------------------------------ *
   * Result screen
   * ------------------------------------------------------------------ */
  var SLOT_LABEL = { start: 'Start here', add: 'Add this' };

  function renderResult() {
    var r = compute();

    /* Score header */
    active.querySelector('[data-deq-res-n]').textContent = String(r.osdi6_score);
    var badge = active.querySelector('[data-deq-res-band]');
    badge.className = 'deq-band deq-band--' + r.osdi6_band;
    badge.querySelector('[data-deq-res-band-label]').textContent = r.osdi6_band_label;

    var isLocate = r.dryeyeq_primary_action === 'LOCATE';

    /* The exam block and the severe-symptom box are banners above the result.
       The pattern result and its products are shown either way: past the
       first-screen safety stop, nobody finishes DryEyeQ without a next step
       they can act on today. */
    var notice = active.querySelector('[data-deq-notice-slot]');
    notice.innerHTML = '';
    if (r.severeNotice) {
      notice.appendChild(tplFor('notice-severe').content.cloneNode(true));
      notice.querySelector('[data-deq-severe]').hidden = false;
    }
    if (isLocate) {
      notice.appendChild(tplFor('notice-exam').content.cloneNode(true));
      notice.querySelector('[data-deq-locate-block]').hidden = false;
    }

    /* Exactly one pattern block and one Shop / Learn / Locate row is cloned
       in. The other five of each never enter the document. */
    var pat = fill(active, '[data-deq-pattern-slot]', 'pat-' + r.dryeyeq_primary_pattern);
    if (pat) {
      var patEl = pat.querySelector('[data-pattern]');
      if (patEl) patEl.hidden = false;
    }
    var cta = fill(active, '[data-deq-cta-slot]', 'cta-' + r.dryeyeq_primary_pattern);
    if (cta) {
      var ctaEl = cta.querySelector('[data-routecta]');
      if (ctaEl) ctaEl.hidden = false;
    }

    /* Products */
    var wrapMain = active.querySelector('[data-deq-cards]');
    wrapMain.innerHTML = '';

    var picks = productsFor(r.dryeyeq_primary_pattern);

    /* Slot labels are positional, so a route whose first-choice products are
       all unavailable still leads with "Start here" rather than "Add this". */
    picks.forEach(function (item, i) { item.slot = i === 0 ? SLOT_LABEL.start : SLOT_LABEL.add; });
    picks.forEach(function (item) { wrapMain.appendChild(buildCard(item.p, item.slot)); });

    /* No curated product available for the route: the category button in the
       Shop / Learn / Locate row is the fallback and is always present, so
       nothing renders here rather than an empty card. */
    active.querySelector('[data-deq-products-block]').hidden = picks.length === 0;

    /* Contact-lens result: raise the exam when symptoms persist after removal */
    var contactNote = active.querySelector('[data-deq-contact-persist]');
    if (contactNote) contactNote.hidden = !(r.dryeyeq_primary_pattern === 'contact' && r.elevateLocate);

    /* Demodex note on the lid-care result */
    var demodexNote = active.querySelector('[data-deq-demodex]');
    if (demodexNote) demodexNote.hidden = !(r.dryeyeq_primary_pattern === 'lid' && state.p6 === 'demodex');

    root.setAttribute('data-deq-action', r.dryeyeq_primary_action);
    root.setAttribute('data-deq-route', isLocate ? 'locate' : r.dryeyeq_primary_pattern);
  }

  function productsFor(pattern) {
    var out = [];
    function add(route, slot, excludeHandle) {
      var p = pickProduct(route, excludeHandle);
      if (p) out.push({ p: p, slot: slot });
      return p;
    }
    if (pattern === 'hydration') {
      add('hydration', SLOT_LABEL.start);
    } else if (pattern === 'mgd') {
      var d = add('mgd-drop', SLOT_LABEL.start);
      add('mgd-heat', SLOT_LABEL.add, d ? d.h : null);
    } else if (pattern === 'lid') {
      var l = add('lid-care', SLOT_LABEL.start);
      add('hydration', SLOT_LABEL.add, l ? l.h : null);
    } else if (pattern === 'night') {
      var n = add('nighttime', SLOT_LABEL.start);
      add('hydration', SLOT_LABEL.add, n ? n.h : null);
    } else if (pattern === 'allergy') {
      add('allergy', SLOT_LABEL.start);
    } else if (pattern === 'contact') {
      var c = add('contact', SLOT_LABEL.start);
      add('hydration', SLOT_LABEL.add, c ? c.h : null);
    }
    return out.slice(0, 2);
  }

  var REASONS = CONFIG.reasons || {};

  function buildCard(p, slot) {
    var card = document.createElement('div');
    card.className = 'deq-card';

    if (slot) {
      var k = document.createElement('p');
      k.className = 'deq-card__slot';
      k.textContent = slot;
      card.appendChild(k);
    }

    if (p.img) {
      var a = document.createElement('a');
      a.className = 'deq-card__imgwrap';
      a.href = p.u;
      a.setAttribute('tabindex', '-1');
      a.setAttribute('aria-hidden', 'true');
      var img = document.createElement('img');
      img.className = 'deq-card__img';
      img.src = p.img;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 400;
      img.height = 300;
      a.appendChild(img);
      card.appendChild(a);
    }

    var body = document.createElement('div');
    body.className = 'deq-card__body';

    if (p.b) {
      var brand = document.createElement('span');
      brand.className = 'deq-card__brand';
      brand.textContent = p.b;
      body.appendChild(brand);
    }

    var title = document.createElement('a');
    title.className = 'deq-card__t';
    title.href = p.u;
    title.textContent = p.t;
    title.setAttribute('data-deq-track', 'dryeyeq_product_click');
    body.appendChild(title);

    var why = REASONS[p.r];
    if (why) {
      var w = document.createElement('p');
      w.className = 'deq-card__why';
      w.textContent = why;
      body.appendChild(w);
    }

    var foot = document.createElement('div');
    foot.className = 'deq-card__foot';
    var price = document.createElement('span');
    price.className = 'deq-card__price';
    price.textContent = p.p;
    foot.appendChild(price);
    if (p.c) {
      var was = document.createElement('span');
      was.className = 'deq-card__was';
      was.textContent = p.c;
      foot.appendChild(was);
    }
    body.appendChild(foot);

    var acts = document.createElement('div');
    acts.className = 'deq-card__acts';
    var atc = document.createElement('button');
    atc.type = 'button';
    atc.className = 'deq-btn deq-btn--primary deq-atc';
    atc.setAttribute('data-deq-atc', '');
    atc.setAttribute('data-variant', String(p.v));
    atc.textContent = 'Add to Cart';
    acts.appendChild(atc);

    var view = document.createElement('a');
    view.className = 'deq-btn deq-btn--secondary';
    view.href = p.u;
    view.textContent = 'View Product';
    view.setAttribute('data-deq-track', 'dryeyeq_product_click');
    acts.appendChild(view);

    body.appendChild(acts);
    card.appendChild(body);
    return card;
  }

  /* ------------------------------------------------------------------ *
   * Add to cart. Stays on the DryEyeQ result and refreshes the theme cart.
   * ------------------------------------------------------------------ */
  var toastTimer;
  function toast(msg) {
    if (!els.toast) return;
    els.toast.textContent = msg;
    els.toast.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () { els.toast.hidden = true; }, 3200);
  }

  function addToCart(btn) {
    var id = btn.getAttribute('data-variant');
    if (!id || btn.disabled) return;
    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
      .then(function (r) {
        if (!r.ok) throw new Error((r.data && r.data.description) || 'Could not add to cart');
        btn.textContent = 'Added';
        btn.classList.add('is-added');
        btn.disabled = false;
        toast('Added to your cart.');
        track('dryeyeq_add_to_cart');
        refreshCart();
      })
      .catch(function (err) {
        btn.disabled = false;
        btn.textContent = original;
        toast(err.message || 'Sorry, that could not be added right now.');
      });
  }

  function refreshCart() {
    try {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    } catch (e) { /* no drawer on this theme */ }
    fetch('?sections=cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var host = document.getElementById('cart-icon-bubble');
        if (!host || !sections['cart-icon-bubble']) return;
        var tmpl = document.createElement('template');
        tmpl.innerHTML = sections['cart-icon-bubble'];
        var inner = tmpl.content.querySelector('.shopify-section');
        host.innerHTML = inner ? inner.innerHTML : tmpl.innerHTML;
      })
      .catch(function () { /* the drawer refresh above is the primary path */ });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  mount('intro');
  updateProgress();

  /* Expose the engine for verification only. No answers are stored here. */
  root.deq = { compute: compute, state: function () { return state; }, go: go, PRODUCTS: PRODUCTS };
})();
